// D3-9 — "LALA REVIEW". THE MONITORING QUEUE.
//
// HER SENTENCE IS THE SPEC: "her duty made workable — surface the conversations the machine flags so
// she monitors BY EXCEPTION instead of reading everything."
//
// So this tab shows NOTHING that is not flagged. That is a design constraint, not a limitation: a
// queue that also shows the healthy turns is a reading list, and a reading list is the thing she
// already cannot get through. Everything here is an owner-facing view over data THE GATES ALREADY
// LOG — no new member-facing behaviour, no paid calls, and no second copy of the gate log, which
// lives in the Gates tab and stays there.
//
// The flag families are NOT decided here. They come down with the payload out of src/usagePage.js,
// where each family names the exact gates it covers — gates measured off the live log, so a family
// wired to a gate name nothing writes is a red fixture rather than a quiet empty week.
import { useCallback, useEffect, useState } from "react";
import { REVIEW_NOTE } from "../usagePage.js";
import { OwnerTranscript } from "./OwnerTranscript";

type Flag = { key: string; label: string; why: string; gates: string[] };
type Row = {
  id: string; gate: string; outcome: string; userId: string | null; email: string; name: string;
  at: string; turnId: string; turnConversationId: string; read: string; flag: string;
};
type Payload = { days: number; flag: string; items: Row[]; trends: Array<{ day: string; gate: string; count: number }>; flags: Flag[]; note: string };

const stamp = (iso: string) => (iso ? new Date(iso).toLocaleString() : "");

export function OwnerReview({ onDenied }: { onDenied: () => void }) {
  const [data, setData] = useState<Payload | null>(null);
  const [flag, setFlag] = useState("");
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(false);
  const [note, setNote] = useState("");
  const [open, setOpen] = useState<Row | null>(null);

  const load = useCallback(() => {
    setLoading(true); setNote("");
    fetch(`/api/owner/support/review?days=${days}${flag ? `&flag=${encodeURIComponent(flag)}` : ""}`, { cache: "no-store" })
      .then((r) => (r.status === 403 ? r.json().then((d) => { if (d?.message === "Not found.") onDenied(); else setNote(d?.message || "Unlock the support passcode on a member to open this."); return null; }) : r.json()))
      .then((d) => { if (d?.ok) setData(d); else if (d) setNote(d.message || "That could not be read."); })
      .catch(() => setNote("That could not be read."))
      .finally(() => setLoading(false));
  }, [days, flag, onDenied]);

  useEffect(() => { load(); }, [load]);

  // Trends collapsed to a day total per family. "Gate trips per day by type", which is her wording,
  // and it is what makes drift visible BEFORE members feel it.
  const byDay = new Map<string, number>();
  for (const t of data?.trends || []) byDay.set(t.day, (byDay.get(t.day) || 0) + t.count);
  const days30 = [...byDay.entries()].sort((a, b) => (a[0] < b[0] ? 1 : -1)).slice(0, 14);
  const peak = Math.max(1, ...days30.map(([, n]) => n));

  if (open) {
    return (
      <OwnerTranscript
        memberId={String(open.userId || "")}
        conversationId={open.turnConversationId || "drawer"}
        highlightTurnId={open.turnId}
        onClose={() => setOpen(null)}
        onDenied={onDenied}
      />
    );
  }

  return (
    <div className="owner-review">
      <p className="owner-page__note">{data?.note || REVIEW_NOTE}</p>
      {note ? <p className="owner-page__note owner-support__walls">{note}</p> : null}

      <div className="owner-bar owner-review__filters">
        <div className="owner-range">
          <button type="button" className={flag === "" ? "owner-range__btn is-active" : "owner-range__btn"} onClick={() => setFlag("")}>All flags</button>
          {(data?.flags || []).map((f) => (
            <button key={f.key} type="button" title={f.why} data-flag-filter={f.key}
              className={flag === f.key ? "owner-range__btn is-active" : "owner-range__btn"}
              onClick={() => setFlag(f.key)}>{f.label}</button>
          ))}
        </div>
        <div className="owner-range">
          {[7, 30, 90].map((d) => (
            <button key={d} type="button" className={days === d ? "owner-range__btn is-active" : "owner-range__btn"} onClick={() => setDays(d)}>{d} days</button>
          ))}
        </div>
      </div>

      {loading ? <p className="owner-page__note">Loading...</p> : null}

      {days30.length ? (
        <>
          <h3 className="owner-detail__sub">Flags per day</h3>
          <ul className="owner-bars owner-review__trend">
            {days30.map(([d, n]) => (
              <li key={d} data-trend-day={d}>
                <span className="owner-bars__label">{d}</span>
                <span className="owner-review__spark" style={{ width: `${Math.round((n / peak) * 100)}%` }} aria-hidden="true" />
                <span className="owner-bars__count">{n}</span>
              </li>
            ))}
          </ul>
        </>
      ) : null}

      <h3 className="owner-detail__sub">Flagged conversations</h3>
      <div className="owner-table-wrap">
        <table className="owner-table">
          <thead>
            <tr><th>When</th><th>Member</th><th>Flag</th><th>Gate</th><th>What it caught</th><th /></tr>
          </thead>
          <tbody>
            {(data?.items || []).map((r) => (
              <tr key={r.id} data-review-row={r.id} data-review-flag={r.flag}>
                <td>{stamp(r.at)}</td>
                <td className="owner-table__email">{r.name || r.email || "—"}</td>
                <td>{(data?.flags || []).find((f) => f.key === r.flag)?.label || r.flag}</td>
                <td>{r.gate}{r.outcome ? ` · ${r.outcome}` : ""}</td>
                <td>{r.read || "—"}</td>
                <td>
                  {/* Straight into the full transcript with the flagged turn highlighted — which is
                      the point of the queue. A row with no member behind it (a gate that fired with
                      no account attached) says so rather than offering a door to nowhere. */}
                  {r.userId
                    ? <button type="button" className="owner-link" onClick={() => setOpen(r)}>Open the turn</button>
                    : <span className="owner-page__note">no account on this event</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!loading && !(data?.items || []).length ? <p className="owner-page__note">Nothing flagged in this window. That is the good outcome.</p> : null}
    </div>
  );
}
